import { InputNumber, Slider, Space } from 'antd';
import PropTypes from 'prop-types';

// How the range is presented. The admin picks this per field, so a team that
// dislikes the horizontal slider can fall back to plain From/To boxes without
// any code change.
const RANGE_DISPLAYS = ['both', 'slider', 'inputs'];

// Value is always [from, to]. The slider and the number boxes are two views of
// that one tuple, so editing either updates the other.
export default function RangeSliderField({ field, value, onChange, disabled }) {
  const min = Number(field.min ?? 0);
  const max = Number(field.max ?? 30);
  const display = RANGE_DISPLAYS.includes(field.display) ? field.display : 'both';

  const current = Array.isArray(value) && value.length === 2 ? value : [undefined, undefined];
  const [from, to] = current;

  // All three are admin-configurable so this stays generic: a salary range would
  // read "Min / Max / USD" without touching this component. `unit` is optional —
  // omit it and the numbers stand alone, since the field's own label usually
  // already names the unit.
  const fromLabel = field.fromLabel ?? 'From';
  const toLabel = field.toLabel ?? 'To';
  const unit = field.unit ?? '';

  // What the two ends READ AS. Blank falls back to the bound itself, so an admin
  // who raises Max from 30 to 40 gets a track labelled 40 without also having to
  // remember to edit the caption.
  const minLabel = String(field.minLabel ?? '').trim() || String(min);
  const maxLabel = String(field.maxLabel ?? '').trim() || String(max);

  // The two ends stay labelled under the track at all times; everything between
  // them is read off the tooltip riding the handle. `marks` is the escape hatch
  // for a mid-point label and wins outright when set.
  const marks = field.marks ?? { [min]: minLabel, [max]: maxLabel };

  // The number boxes DO borrow the end labels — the To box reads "30+" when it
  // sits on the ceiling. The tooltip deliberately does not: it tracks a handle
  // being dragged, where a label that stops counting at the last step reads as
  // the drag having stuck.
  //
  // While the user is mid-keystroke their own text is echoed back untouched.
  // Substituting the label there would fight the caret: typing "3" then "0"
  // against a max of 30 would rewrite the box to "30+" and send the next
  // keystroke after the plus.
  const formatBound = (val, info) => {
    if (info?.userTyping) return info.input;
    if (val === null || val === undefined || val === '') return '';
    if (Number(val) === min) return minLabel;
    if (Number(val) === max) return maxLabel;
    return String(val);
  };

  // Inverse of formatBound: what the user sees typed back into a number antd can
  // hold. Anything else falls back to stripping non-numerics, so pasting "12 yrs"
  // still lands on 12 rather than clearing the box.
  const parseBound = (text) => {
    const raw = String(text ?? '').trim();
    if (raw === '') return '';
    if (raw === maxLabel) return String(max);
    if (raw === minLabel) return String(min);
    return raw.replace(/[^0-9.-]/g, '');
  };

  const emit = (next) => {
    const lo = next[0] === null || next[0] === undefined || next[0] === '' ? undefined : Number(next[0]);
    const hi = next[1] === null || next[1] === undefined || next[1] === '' ? undefined : Number(next[1]);
    if (lo === undefined && hi === undefined) {
      onChange(undefined);
      return;
    }
    // Dragging the low handle past the high one (or typing 10 then 2) would
    // otherwise send an inverted range the backend rejects.
    if (lo !== undefined && hi !== undefined) {
      onChange([Math.min(lo, hi), Math.max(lo, hi)]);
      return;
    }
    onChange([lo, hi]);
  };

  return (
    <div className="sbv1-range">
      {display !== 'inputs' && (
        <Slider
          range
          min={min}
          max={max}
          marks={marks}
          disabled={disabled}
          value={[from ?? min, to ?? max]}
          // The marks label only the two END points, so a handle dragged
          // somewhere in between would otherwise show no number at all. antd's
          // default tooltip rides the handle showing the raw number, which is
          // exactly the reading wanted — the value stays where the eye already
          // is, and it keeps counting right up to the ceiling.
          onChange={emit}
        />
      )}
      {display !== 'slider' && (
        <Space size={8} className="sbv1-range-inputs">
          {/* Visible labels rather than placeholders: a placeholder disappears
              the moment a value is typed, leaving two bare boxes with no way to
              tell which end is which. */}
          <span className="sbv1-range-label">{fromLabel}</span>
          <InputNumber
            size="small"
            min={min}
            max={max}
            disabled={disabled}
            value={from ?? null}
            placeholder={minLabel}
            aria-label={fromLabel}
            formatter={formatBound}
            parser={parseBound}
            onChange={(next) => emit([next, to])}
          />
          <span className="sbv1-range-label">{toLabel}</span>
          <InputNumber
            size="small"
            min={min}
            max={max}
            disabled={disabled}
            value={to ?? null}
            placeholder={maxLabel}
            aria-label={toLabel}
            formatter={formatBound}
            parser={parseBound}
            onChange={(next) => emit([from, next])}
          />
          {unit && <span className="sbv1-range-label">{unit}</span>}
        </Space>
      )}
    </div>
  );
}

RangeSliderField.propTypes = {
  field: PropTypes.object.isRequired,
  value: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};
